import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { CheckCircle2, Users, Award, Radio, MessageSquare, Wrench } from "lucide-react";

const benefits = [
    { icon: Users, title: "Skúsený technický tím", desc: "Zohratý tím profesionálov s praxou v medzinárodných produkciách." },
    { icon: Award, title: "Profesionálna technika", desc: "Pracujeme s profesionálnou a certifikovanou technikou." },
    { icon: Wrench, title: "Individuálne riešenia", desc: "Každý event je iný. Technické riešenie staviame na mieru vášmu priestoru." },
    { icon: CheckCircle2, title: "Spoľahlivosť na mieste", desc: "Záložné systémy, rýchle reakcie a technik prítomný počas celého eventu." },
    { icon: MessageSquare, title: "Rýchla komunikácia", desc: "Cenová ponuka do 24 hodín. Otvorená a priama komunikácia." },
    { icon: Radio, title: "Realizácie na mieru", desc: "Od intímneho workshopu po festival pre 10 000 ľudí – všetko zvládame." },
];

function Counter({ target, suffix = "", duration = 2000 }) {
    const [n, setN] = useState(0);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (!inView) return;
        let start = 0;
        const end = parseInt(target);
        const startTime = performance.now();
        const tick = (now) => {
            const p = Math.min((now - startTime) / duration, 1);
            setN(Math.floor(start + (end - start) * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [inView, target, duration]);

    return (
        <span ref={ref}>
            {n}
            {suffix}
        </span>
    );
}

export default function WhyUs() {
    return (
        <section
            id="why-us"
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="why-us-section"
        >
            <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-nova-cyan/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-6"
                    >
                        <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                            / 03 — Prečo KB MUSIC
                        </div>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                            Detaily rozhodujú.{" "}
                            <span className="text-gradient">My ich vieme.</span>
                        </h2>
                    </motion.div>
                </div>

                {/* Benefits grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden">
                    {benefits.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <motion.div
                                key={b.title}
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="p-8 bg-nova-black hover:bg-nova-surface transition-colors group"
                                data-testid={`benefit-${i}`}
                            >
                                <Icon
                                    className="w-7 h-7 text-nova-cyan mb-5 group-hover:text-nova-purple transition-colors"
                                    strokeWidth={1.8}
                                />
                                <h4 className="font-heading text-lg font-bold text-white mb-2">
                                    {b.title}
                                </h4>
                                <p className="text-sm text-zinc-500 leading-relaxed">
                                    {b.desc}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
